
import { clsx, type ClassValue } from "clsx"
import { format, parseISO } from "date-fns"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength)}...`
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null
  
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

export function generateRandomId(): string {
  return Math.random().toString(36).substring(2, 15)
}

export function calculateTimeAgo(date: Date | string): string {
  const seconds = Math.floor((new Date().getTime() - new Date(date).getTime()) / 1000)
  
  let interval = seconds / 31536000
  if (interval > 1) return Math.floor(interval) + ' years ago'
  
  interval = seconds / 2592000
  if (interval > 1) return Math.floor(interval) + ' months ago'
  
  interval = seconds / 86400
  if (interval > 1) return Math.floor(interval) + ' days ago'
  
  interval = seconds / 3600
  if (interval > 1) return Math.floor(interval) + ' hours ago'
  
  interval = seconds / 60
  if (interval > 1) return Math.floor(interval) + ' minutes ago'
  
  return Math.floor(seconds) + ' seconds ago'
}

// New utility functions for the project
export function generateProjectId(): string {
  return 'proj-' + Date.now().toString(36) + Math.random().toString(36).substring(2, 5);
}

export function generatePostId(): string {
  return 'post-' + Date.now().toString(36) + Math.random().toString(36).substring(2, 5);
}

export function filterProjects(projects: any[], filters: {
  searchTerm?: string;
  category?: string;
  budget?: string;
}): any[] {
  return projects.filter(project => {
    const matchesSearch = !filters.searchTerm || 
      project.title.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      project.description.toLowerCase().includes(filters.searchTerm.toLowerCase());
    
    const matchesCategory = !filters.category || filters.category === "All" || 
      project.category === filters.category;
    
    const matchesBudget = !filters.budget || filters.budget === "All" || 
      (filters.budget === "Under $1,000" && parseInt(project.budget.split('$')[1].split(' ')[0].replace(',', '')) < 1000) ||
      (filters.budget === "$1,000 - $5,000" && 
        parseInt(project.budget.split('$')[1].split(' ')[0].replace(',', '')) >= 1000 && 
        parseInt(project.budget.split('$')[2].replace(',', '')) <= 5000) ||
      (filters.budget === "$5,000 - $10,000" && 
        parseInt(project.budget.split('$')[1].split(' ')[0].replace(',', '')) >= 5000 && 
        parseInt(project.budget.split('$')[2].replace(',', '')) <= 10000) ||
      (filters.budget === "$10,000+" && 
        parseInt(project.budget.split('$')[1].split(' ')[0].replace(',', '')) >= 10000);
    
    return matchesSearch && matchesCategory && matchesBudget;
  });
}

export function shuffleArray<T>(array: T[]): T[] {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
}

export const dateFormat = (dateString: string | undefined) => {
  if (!dateString) return "";
  try {
    return format(parseISO(dateString), "yyyy-MM-dd");
  } catch (err) {
    return "";
  }
};

export const getImageUrl = (profilePicture) => {
  if (!profilePicture) return null;
  
  const BASE_URL = import.meta.env.VITE_APP_BASE_URL|| 'http://localhost:5001';

  const normalizedPath = profilePicture.replace(/\\/g, '/');

  const relativePath = normalizedPath.includes('/uploads')
    ? normalizedPath.substring(normalizedPath.indexOf('/uploads'))
    : null;

  return relativePath ? `${BASE_URL}${relativePath}` : null;
};

export const capitalizeFirst = (text?: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : '';